import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import MessageTemplatePreview, { TYPE_META } from '../../components/MessageTemplatePreview';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, Legend, LineChart, Line
} from 'recharts';
import {
  FiArrowLeft, FiCheckCircle, FiXCircle, FiClock, FiDatabase,
  FiSearch, FiFileText, FiBarChart2, FiTrendingUp, FiSend,
  FiTarget, FiThumbsUp, FiPercent, FiArrowUpRight
} from 'react-icons/fi';

/* ── Animated Number Counter ── */
const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const numVal = typeof value === 'string' ? parseInt(value.replace(/,/g, ''), 10) : value;
  useEffect(() => {
    if (isNaN(numVal) || numVal === 0) { setDisplay(0); return; }
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * numVal));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [numVal, duration]);
  return <>{display.toLocaleString()}</>;
};

/* ── Shared helpers ── */
const COLORS = { green: '#00B4D8', red: '#1a1a18', orange: '#9ec25a', sky: '#00B4D8', ink: '#1a1a18' };
const PIE_COLORS = ['#00B4D8', '#1a1a18', '#9ec25a', '#5c7a1f', '#48CAE4', '#455a19', '#c9c9c3', '#7e7e71', '#abab9f', '#e6e6e3'];

const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email',
  rcs: 'RCS', newsletter: 'Newsletter', push_notification: 'Push',
  voice_call: 'Voice', status_messaging: 'Status', custom: 'Custom'
};
const TYPE_COLORS = {
  whatsapp: '#00B4D8', whatsapp_image: '#6b8f27', whatsapp_video: '#57721f',
  whatsapp_document: '#455a19', sms: '#9ec25a', email: '#1a1a18',
  rcs: '#48CAE4', newsletter: '#5c7a1f', push_notification: '#7e7e71',
  voice_call: '#abab9f', status_messaging: '#161614', custom: '#c9c9c3'
};

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)', border: '1px solid rgba(0,180,216,0.12)', borderRadius: 14, padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
      {label && <p style={{ color: '#0f172a', fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'Poppins, sans-serif' }}>{label}</p>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill }} />
          <span style={{ color: '#475569', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const CustomLegend = ({ payload }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingTop: 8 }}>
    {payload?.map((e, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: e.color }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{e.value}</span>
      </div>
    ))}
  </div>
);

const PieCenter = ({ cx, cy, label, value }) => (
  <g>
    <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontSize: 20, fontWeight: 800, fill: '#0f172a', fontFamily: 'Poppins' }}>{value}</text>
    <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</text>
  </g>
);

const SectionHeader = ({ title, subtitle }) => (
  <div style={{ marginBottom: 14 }}>
    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
    {subtitle && <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', fontWeight: 500 }}>{subtitle}</p>}
  </div>
);

/* ── Shared SVG Chart Tooltip ── */
const ChartTooltipStyle = {
  position: 'absolute', pointerEvents: 'none', zIndex: 50,
  background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(16px)',
  border: '1px solid rgba(0,180,216,0.15)', borderRadius: 12,
  padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,180,216,0.06)',
  fontSize: 12, fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap',
  transition: 'opacity 0.15s ease', minWidth: 90,
};
const ChartTooltip = ({ tip, containerRef }) => {
  if (!tip) return null;
  return (
    <div style={{ ...ChartTooltipStyle, left: tip.x + 12, top: tip.y - 10, opacity: 1 }}>
      {tip.label && <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4, letterSpacing: 0.3 }}>{tip.label}</div>}
      {tip.items && tip.items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
          {item.color && <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, flexShrink: 0 }} />}
          <span style={{ color: '#475569' }}>{item.name}:</span>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{typeof item.value === 'number' ? item.value.toLocaleString() : item.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Hive Plot – Custom SVG network chart ── */
const HivePlot = ({ data }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 260, H = 220, cx = W / 2, cy = H / 2 - 5;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const R = 80;
  const nodes = data.map((d, i) => {
    const angle = (i / data.length) * 2 * Math.PI - Math.PI / 2;
    const ratio = d.value / maxVal;
    const dist = R * 0.25 + R * 0.75 * ratio;
    return { ...d, i, x: cx + Math.cos(angle) * dist, y: cy + Math.sin(angle) * dist,
      ex: cx + Math.cos(angle) * R, ey: cy + Math.sin(angle) * R, nr: 6 + ratio * 14 };
  });
  const onHover = (e, n) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: n.name, items: [{ name: 'Value', value: n.value, color: n.color }] });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <filter id="hvSh"><feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.15"/></filter>
        {nodes.map((n, i) => (
          <radialGradient key={i} id={`hv${i}`} cx="35%" cy="35%">
            <stop offset="0%" stopColor={n.color} stopOpacity="1"/><stop offset="100%" stopColor={n.color} stopOpacity="0.55"/>
          </radialGradient>
        ))}
      </defs>
      {[0.33, 0.66, 1].map((r, i) => <circle key={i} cx={cx} cy={cy} r={R * r} fill="none" stroke="#e2e8f0" strokeWidth={0.7} strokeDasharray="3 4" opacity={0}>
        <animate attributeName="opacity" from="0" to="0.5" dur="0.6s" begin={`${i * 0.15}s`} fill="freeze"/>
        <animate attributeName="r" from="0" to={R * r} dur="0.8s" begin={`${i * 0.15}s`} fill="freeze"/>
      </circle>)}
      {nodes.map((n, i) => <line key={`a${i}`} x1={cx} y1={cy} x2={cx} y2={cy} stroke="#cbd5e1" strokeWidth={1}>
        <animate attributeName="x2" from={cx} to={n.ex} dur="0.6s" begin={`${0.3 + i * 0.1}s`} fill="freeze"/>
        <animate attributeName="y2" from={cy} to={n.ey} dur="0.6s" begin={`${0.3 + i * 0.1}s`} fill="freeze"/>
      </line>)}
      {nodes.map((n, i) => {
        const nxt = nodes[(i + 1) % nodes.length];
        const pathLen = 200;
        return <path key={`l${i}`} d={`M${n.x},${n.y} Q${cx},${cy} ${nxt.x},${nxt.y}`} fill="none" stroke={n.color} strokeWidth={2} opacity={0.25} strokeLinecap="round"
          strokeDasharray={pathLen} strokeDashoffset={pathLen}>
          <animate attributeName="stroke-dashoffset" from={pathLen} to="0" dur="1s" begin={`${0.5 + i * 0.12}s`} fill="freeze"/>
        </path>;
      })}
      <circle cx={cx} cy={cy} r={3.5} fill="#1a1a18" opacity={0}>
        <animate attributeName="opacity" from="0" to="0.2" dur="0.4s" begin="0.2s" fill="freeze"/>
      </circle>
      {nodes.map((n, i) => (
        <g key={`n${i}`} filter="url(#hvSh)" opacity="0" transform={`translate(${n.x},${n.y}) scale(0)`}
          style={{ cursor: 'pointer' }} onMouseMove={e => onHover(e, n)} onMouseLeave={() => setTip(null)}>
          <animateTransform attributeName="transform" type="translate" from={`${cx} ${cy}`} to={`${n.x} ${n.y}`} dur="0.7s" begin={`${0.4 + i * 0.12}s`} fill="freeze"/>
          <animateTransform attributeName="transform" type="scale" from="0" to="1" dur="0.5s" begin={`${0.5 + i * 0.12}s`} fill="freeze" additive="sum"/>
          <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.4 + i * 0.12}s`} fill="freeze"/>
          <circle cx={0} cy={0} r={n.nr} fill={`url(#hv${i})`} stroke="#fff" strokeWidth={2.5}/>
          <circle cx={-n.nr * 0.25} cy={-n.nr * 0.25} r={n.nr * 0.3} fill="#fff" opacity={0.3}/>
          <text x={0} y={n.nr + 13} textAnchor="middle" fontSize="9" fontWeight="600" fill="#64748b">{n.name}</text>
          {n.nr > 10 && <text x={0} y={3.5} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">{n.value}</text>}
        </g>
      ))}
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

/* ── 3D Line Chart – Custom SVG with perspective depth ── */
const Line3D = ({ data }) => {
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 480, H = 280, pad = { t: 20, r: 25, b: 35, l: 45 };
  const w = W - pad.l - pad.r, h = H - pad.t - pad.b;
  const dx = 14, dy = -10;
  const keys = ['Sent', 'Failed', 'Pending'], colors = ['#00B4D8', '#1a1a18', '#9ec25a'];
  const maxV = Math.max(...data.flatMap(d => keys.map(k => d[k] || 0)), 1);
  const gx = i => pad.l + (i / Math.max(data.length - 1, 1)) * w;
  const gy = v => pad.t + h - (v / maxV) * h;
  const lp = key => data.map((d, i) => `${i ? 'L' : 'M'}${gx(i)},${gy(d[key] || 0)}`).join('');
  const ap = key => { const pts = data.map((d, i) => ({ x: gx(i), y: gy(d[key] || 0) }));
    return pts.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join('') + `L${pts[pts.length - 1].x},${gy(0)} L${pts[0].x},${gy(0)} Z`; };
  const yT = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(maxV * t));
  const lineLen = 800;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        {colors.map((c, i) => <linearGradient key={i} id={`l3f${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c} stopOpacity="0.2"/><stop offset="100%" stopColor={c} stopOpacity="0.02"/></linearGradient>)}
        <filter id="l3s"><feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.12"/></filter>
      </defs>
      {yT.map((t, i) => <g key={i} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${i * 0.08}s`} fill="freeze"/>
        <line x1={pad.l} y1={gy(t)} x2={pad.l + w} y2={gy(t)} stroke="#e2e8f0" strokeWidth={0.8} strokeDasharray="4 3"/>
        <line x1={pad.l + w} y1={gy(t)} x2={pad.l + w + dx} y2={gy(t) + dy} stroke="#e2e8f0" strokeWidth={0.5} strokeDasharray="3 3"/>
        <text x={pad.l - 6} y={gy(t) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{t}</text></g>)}
      {data.map((d, i) => <text key={i} x={gx(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#94a3b8" fontWeight="500" opacity="0">
        <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${0.3 + i * 0.1}s`} fill="freeze"/>{d.name}</text>)}
      {keys.map((k, ki) => <g key={`bg${ki}`} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.8s" begin={`${0.6 + ki * 0.15}s`} fill="freeze"/>
        <path d={lp(k)} fill="none" stroke={colors[ki]} strokeWidth={2.5} opacity={0.1} transform={`translate(${dx},${dy})`}/>
        {data.map((d, i) => <line key={i} x1={gx(i)} y1={gy(d[k] || 0)} x2={gx(i) + dx} y2={gy(d[k] || 0) + dy} stroke={colors[ki]} strokeWidth={0.5} opacity={0.1}/>)}</g>)}
      {keys.map((k, ki) => <g key={`fg${ki}`} filter="url(#l3s)">
        <path d={ap(k)} fill={`url(#l3f${ki})`} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.8s" begin={`${0.5 + ki * 0.2}s`} fill="freeze"/></path>
        <path d={lp(k)} fill="none" stroke={colors[ki]} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={lineLen} strokeDashoffset={lineLen}>
          <animate attributeName="stroke-dashoffset" from={lineLen} to="0" dur="1.2s" begin={`${0.4 + ki * 0.2}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
        </path>
        {data.map((d, i) => <circle key={i} cx={gx(i)} cy={gy(d[k] || 0)} r={0} fill="#fff" stroke={colors[ki]} strokeWidth={2.5}>
          <animate attributeName="r" from="0" to="4" dur="0.35s" begin={`${0.7 + ki * 0.2 + i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
        </circle>)}</g>)}
      <g transform={`translate(${pad.l}, ${H - 5})`} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.5s" begin="1.2s" fill="freeze"/>
        {keys.map((k, ki) => <g key={ki} transform={`translate(${ki * 80}, 0)`}><rect y={-8} width={10} height={10} rx={2} fill={colors[ki]}/><text x={14} fontSize="10" fill="#64748b" fontWeight="600">{k}</text></g>)}</g>
    </svg>
  );
};

/* ── Alluvial / Sankey Diagram ── */
const AlluvialDiagram = ({ data }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 420, H = 260, pad = 30, colW = 14;
  const total = data.reduce((s, d) => s + (d.sent || 0) + (d.failed || 0), 0) || 1;
  const left = data.map(d => ({ name: d.name, val: (d.sent || 0) + (d.failed || 0) })).filter(d => d.val > 0);
  const right = [{ name: 'Sent', val: data.reduce((s, d) => s + (d.sent || 0), 0), color: '#00B4D8' },
    { name: 'Failed', val: data.reduce((s, d) => s + (d.failed || 0), 0), color: '#1a1a18' }].filter(d => d.val > 0);
  const usableH = H - pad * 2;
  let lY = pad;
  const lNodes = left.map(d => { const h = Math.max((d.val / total) * usableH, 12); const n = { ...d, y: lY, h }; lY += h + 4; return n; });
  let rY = pad;
  const rNodes = right.map(d => { const h = Math.max((d.val / total) * usableH, 12); const n = { ...d, y: rY, h }; rY += h + 4; return n; });
  const lx = pad, rx = W - pad - colW;
  const flows = [];
  let rOff = {}; right.forEach(r => rOff[r.name] = 0);
  let lOff = {}; left.forEach(l => lOff[l.name] = 0);
  data.forEach((d, i) => {
    const ln = lNodes.find(n => n.name === d.name); if (!ln) return;
    ['Sent', 'Failed'].forEach(key => {
      const val = key === 'Sent' ? (d.sent || 0) : (d.failed || 0); if (!val) return;
      const rn = rNodes.find(n => n.name === key); if (!rn) return;
      const sH = (val / total) * usableH;
      const sy = ln.y + (lOff[ln.name] || 0), ey = rn.y + (rOff[key] || 0);
      lOff[ln.name] = (lOff[ln.name] || 0) + sH; rOff[key] = (rOff[key] || 0) + sH;
      flows.push({ sy, ey, h: sH, color: rn.color, val, from: d.name, to: key });
    });
  });
  const onHover = (e, label) => { if (!ref.current) return; const r = ref.current.getBoundingClientRect(); setTip({ x: e.clientX - r.left + 10, y: e.clientY - r.top - 10, label }); };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        {flows.map((f, i) => <linearGradient key={i} id={`alv${i}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={f.color} stopOpacity={0.35}/><stop offset="100%" stopColor={f.color} stopOpacity={0.6}/></linearGradient>)}
        <filter id="alvSh"><feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.08"/></filter>
      </defs>
      {flows.map((f, i) => {
        const x1 = lx + colW, x2 = rx, cp = (x2 - x1) * 0.5;
        const pct = ((f.val / total) * 100).toFixed(1);
        return <path key={i} d={`M${x1},${f.sy} C${x1+cp},${f.sy} ${x2-cp},${f.ey} ${x2},${f.ey} L${x2},${f.ey+f.h} C${x2-cp},${f.ey+f.h} ${x1+cp},${f.sy+f.h} ${x1},${f.sy+f.h} Z`}
          fill={`url(#alv${i})`} stroke={f.color} strokeWidth={0.3} strokeOpacity={0.3} opacity="0"
          style={{ cursor: 'pointer' }}
          onMouseEnter={e => onHover(e, `${f.from} → ${f.to}: ${f.val.toLocaleString()} (${pct}%)`)}
          onMouseMove={e => onHover(e, `${f.from} → ${f.to}: ${f.val.toLocaleString()} (${pct}%)`)}
          onMouseLeave={() => setTip(null)}>
          <animate attributeName="opacity" from="0" to="1" dur="0.8s" begin={`${0.3 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
          </path>;
      })}
      {lNodes.map((n, i) => <g key={`l${i}`} filter="url(#alvSh)" opacity="0"
        style={{ cursor: 'pointer' }}
        onMouseEnter={e => onHover(e, `${n.name}: ${n.val.toLocaleString()} (${((n.val / total) * 100).toFixed(1)}%)`)}
        onMouseMove={e => onHover(e, `${n.name}: ${n.val.toLocaleString()} (${((n.val / total) * 100).toFixed(1)}%)`)}
        onMouseLeave={() => setTip(null)}>
        <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${i * 0.1}s`} fill="freeze"/>
        <rect x={lx} y={n.y + n.h / 2} width={colW} height={0} rx={4} fill={PIE_COLORS[i % PIE_COLORS.length]}>
          <animate attributeName="y" from={n.y + n.h / 2} to={n.y} dur="0.6s" begin={`${i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
          <animate attributeName="height" from="0" to={n.h} dur="0.6s" begin={`${i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
        </rect>
        <text x={lx - 4} y={n.y + n.h / 2 + 4} textAnchor="end" fontSize="9" fontWeight="600" fill="#64748b">{n.name}</text></g>)}
      {rNodes.map((n, i) => <g key={`r${i}`} filter="url(#alvSh)" opacity="0"
        style={{ cursor: 'pointer' }}
        onMouseEnter={e => onHover(e, `${n.name}: ${n.val.toLocaleString()} (${((n.val / total) * 100).toFixed(1)}%)`)}
        onMouseMove={e => onHover(e, `${n.name}: ${n.val.toLocaleString()} (${((n.val / total) * 100).toFixed(1)}%)`)}
        onMouseLeave={() => setTip(null)}>
        <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.2 + i * 0.1}s`} fill="freeze"/>
        <rect x={rx} y={n.y + n.h / 2} width={colW} height={0} rx={4} fill={n.color}>
          <animate attributeName="y" from={n.y + n.h / 2} to={n.y} dur="0.6s" begin={`${0.2 + i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
          <animate attributeName="height" from="0" to={n.h} dur="0.6s" begin={`${0.2 + i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
        </rect>
        <text x={rx + colW + 4} y={n.y + n.h / 2 + 4} fontSize="9" fontWeight="600" fill="#64748b">{n.name}</text>
        <text x={rx + colW + 4} y={n.y + n.h / 2 + 15} fontSize="8" fill="#94a3b8">{n.val}</text></g>)}
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

/* ── Arc Diagram ── */
const ArcDiagram = ({ sent, failed, assigned }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  const W = 560, H = 220, baseY = H - 35;
  const nodes = [
    { name: 'Assigned', val: assigned || 0, color: '#00B4D8', x: W * 0.15 },
    { name: 'Sent', val: sent || 0, color: '#9ec25a', x: W * 0.5 },
    { name: 'Failed', val: failed || 0, color: '#1a1a18', x: W * 0.85 },
  ];
  const maxV = Math.max(...nodes.map(n => n.val), 1);
  const links = [
    { s: 0, t: 1, val: sent || 0, color: '#00B4D8' },
    { s: 0, t: 2, val: failed || 0, color: '#1a1a18' },
    { s: 1, t: 2, val: Math.min(sent, failed) || 0, color: '#9ec25a' },
  ].filter(l => l.val > 0);
  const onHover = (e, n) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: n.name, items: [{ name: 'Count', value: n.val, color: n.color }] });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <filter id="arcSh"><feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12"/></filter>
        {nodes.map((n, i) => <radialGradient key={i} id={`arcN${i}`} cx="35%" cy="35%">
          <stop offset="0%" stopColor={n.color} stopOpacity="1"/><stop offset="100%" stopColor={n.color} stopOpacity="0.6"/></radialGradient>)}
      </defs>
      <line x1={W * 0.08} y1={baseY} x2={W * 0.08} y2={baseY} stroke="#e2e8f0" strokeWidth={1.5}>
        <animate attributeName="x2" from={W * 0.08} to={W * 0.92} dur="0.7s" fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
      </line>
      {links.map((l, i) => {
        const sx = nodes[l.s].x, tx = nodes[l.t].x;
        const r = Math.abs(tx - sx) / 2;
        const thick = 1.5 + (l.val / maxV) * 6;
        const mx = (sx + tx) / 2;
        const arcLen = Math.PI * r;
        return <g key={i}><path d={`M${sx},${baseY} A${r},${r} 0 0,1 ${tx},${baseY}`}
          fill="none" stroke={l.color} strokeWidth={thick} opacity={0.2}
          strokeDasharray={arcLen} strokeDashoffset={arcLen}>
          <animate attributeName="stroke-dashoffset" from={arcLen} to="0" dur="1s" begin={`${0.4 + i * 0.15}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
        </path>
          <path d={`M${sx},${baseY} A${r},${r} 0 0,1 ${tx},${baseY}`}
          fill="none" stroke={l.color} strokeWidth={thick * 0.6} opacity={0} strokeDasharray="6 3">
          <animate attributeName="opacity" from="0" to="0.5" dur="0.5s" begin={`${0.9 + i * 0.15}s`} fill="freeze"/>
        </path>
          <text x={mx} y={baseY - r - 4} textAnchor="middle" fontSize="9" fontWeight="600" fill={l.color} opacity={0}>
            <animate attributeName="opacity" from="0" to="0.7" dur="0.4s" begin={`${1.1 + i * 0.15}s`} fill="freeze"/>{l.val}</text>
        </g>;
      })}
      {nodes.map((n, i) => {
        const r = 12 + (n.val / maxV) * 18;
        return <g key={i} filter="url(#arcSh)" opacity="0" style={{ cursor: 'pointer' }}
          onMouseMove={e => onHover(e, n)} onMouseLeave={() => setTip(null)}>
          <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.3 + i * 0.2}s`} fill="freeze"/>
          <circle cx={n.x} cy={baseY} r={0} fill={`url(#arcN${i})`} stroke="#fff" strokeWidth={3}>
            <animate attributeName="r" from="0" to={r} dur="0.6s" begin={`${0.3 + i * 0.2}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
          </circle>
          <circle cx={n.x - r * 0.2} cy={baseY - r * 0.2} r={r * 0.25} fill="#fff" opacity={0.3}/>
          <text x={n.x} y={baseY + r + 16} textAnchor="middle" fontSize="10" fontWeight="700" fill="#334155" opacity="0">
            <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.7 + i * 0.2}s`} fill="freeze"/>{n.name}</text>
          <text x={n.x} y={baseY + r + 28} textAnchor="middle" fontSize="9" fill="#94a3b8" opacity="0">
            <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.8 + i * 0.2}s`} fill="freeze"/>{n.val.toLocaleString()}</text>
          {r > 16 && <text x={n.x} y={baseY + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" opacity="0">
            <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${0.5 + i * 0.2}s`} fill="freeze"/>{n.val}</text>}
        </g>;
      })}
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

/* ── 3D Funnel Chart ── */
const Funnel3D = ({ data }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 280, H = 240, cx = W / 2, topY = 18;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const depth = 12;
  const layers = data.map((d, i) => {
    const ratio = d.value / total;
    const layerH = Math.max(30, (H - topY - 30) * ratio);
    return { ...d, ratio, layerH };
  });
  let y = topY;
  const rects = layers.map((l, i) => {
    const topW = W * 0.85 * (1 - i * 0.18);
    const botW = W * 0.85 * (1 - (i + 1) * 0.18);
    const r = { ...l, y, topW, botW, h: l.layerH };
    y += l.layerH + 2;
    return r;
  });
  const darken = (hex, amt) => '#' + hex.replace('#', '').match(/../g).map(c => Math.max(0, parseInt(c, 16) - amt).toString(16).padStart(2, '0')).join('');
  const onHover = (e, r) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: r.name, items: [{ name: 'Value', value: r.value, color: r.color || PIE_COLORS[0] }, { name: 'Share', value: Math.round(r.ratio * 100) + '%' }] });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width="100%" height={H + 10} viewBox={`0 0 ${W} ${H + 10}`}>
      <defs>
        <filter id="f3sh"><feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.15"/></filter>
        {rects.map((r, i) => <linearGradient key={i} id={`fn3d${i}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={darken(r.color || PIE_COLORS[i], 15)}/><stop offset="50%" stopColor={r.color || PIE_COLORS[i]}/><stop offset="100%" stopColor={darken(r.color || PIE_COLORS[i], 30)}/></linearGradient>)}
      </defs>
      {rects.map((r, i) => {
        const x1 = cx - r.topW / 2, x2 = cx + r.topW / 2;
        const x3 = cx + r.botW / 2, x4 = cx - r.botW / 2;
        const front = `M${x1},${r.y} L${x2},${r.y} L${x3},${r.y + r.h} L${x4},${r.y + r.h} Z`;
        const side = `M${x2},${r.y} L${x2 + depth},${r.y - depth * 0.6} L${x3 + depth},${r.y + r.h - depth * 0.6} L${x3},${r.y + r.h} Z`;
        const top = `M${x1},${r.y} L${x1 + depth},${r.y - depth * 0.6} L${x2 + depth},${r.y - depth * 0.6} L${x2},${r.y} Z`;
        return <g key={i} filter="url(#f3sh)" opacity="0" transform={`translate(0, -20)`}
          style={{ cursor: 'pointer' }} onMouseMove={e => onHover(e, r)} onMouseLeave={() => setTip(null)}>
          <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${i * 0.15}s`} fill="freeze"/>
          <animateTransform attributeName="transform" type="translate" from="0 -20" to="0 0" dur="0.6s" begin={`${i * 0.15}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
          <path d={side} fill={darken(r.color || PIE_COLORS[i], 40)} opacity={0.7}/>
          <path d={top} fill={darken(r.color || PIE_COLORS[i], -20)} opacity={0.85}/>
          <path d={front} fill={`url(#fn3d${i})`}/>
          <text x={cx} y={r.y + r.h / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{r.name}</text>
          <text x={cx + r.topW / 2 + depth + 6} y={r.y + r.h / 2 + 2} fontSize="9" fontWeight="600" fill="#64748b">{r.value}</text>
        </g>;
      })}
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

/* ── Wave 3D Chart – Custom SVG 3D wave ribbons ── */
const Wave3D = ({ data }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 480, H = 240, pad = { t: 20, r: 30, b: 35, l: 48 };
  const w = W - pad.l - pad.r, h = H - pad.t - pad.b;
  const dx = 16, dy = -10;
  const series = ['Sent', 'Failed', 'Pending'];
  const fills = ['#00B4D8', '#1a1a18', '#9ec25a'];
  const maxV = 100;
  const gx = i => pad.l + (i / Math.max(data.length - 1, 1)) * w;
  const gy = v => pad.t + h - (v / maxV) * h;
  const wavePath = key => {
    if (data.length < 2) return data.map((d, i) => `${i ? 'L' : 'M'}${gx(i)},${gy(d[key] || 0)}`).join('');
    let p = `M${gx(0)},${gy(data[0][key] || 0)}`;
    for (let i = 0; i < data.length - 1; i++) {
      const x0 = gx(i), y0 = gy(data[i][key] || 0);
      const x1 = gx(i + 1), y1 = gy(data[i + 1][key] || 0);
      const cp = (x1 - x0) * 0.4;
      p += ` C${x0 + cp},${y0} ${x1 - cp},${y1} ${x1},${y1}`;
    }
    return p;
  };
  const waveArea = key => {
    const lp = wavePath(key);
    const lastX = gx(data.length - 1), baseY = gy(0), firstX = gx(0);
    return lp + ` L${lastX},${baseY} L${firstX},${baseY} Z`;
  };
  const yTicks = [0, 25, 50, 75, 100];
  const pathLen = 900;
  const onHover = (e, d, k, ki) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: d.name, items: series.map((s, si) => ({ name: s, value: (d[s] || 0) + '%', color: fills[si] })) });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        {fills.map((c, i) => <linearGradient key={`wf${i}`} id={`wv3f${i}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.35"/><stop offset="100%" stopColor={c} stopOpacity="0.03"/></linearGradient>)}
        <filter id="wv3sh"><feDropShadow dx="1" dy="2" stdDeviation="3" floodOpacity="0.12"/></filter>
        <filter id="wv3gl"><feGaussianBlur stdDeviation="4" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      {/* 3D back plane */}
      <path d={`M${pad.l + dx},${pad.t + dy} L${pad.l + w + dx},${pad.t + dy} L${pad.l + w + dx},${pad.t + h + dy} L${pad.l + dx},${pad.t + h + dy} Z`}
        fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.5" opacity="0">
        <animate attributeName="opacity" from="0" to="0.5" dur="0.5s" fill="freeze"/>
      </path>
      {/* Depth lines */}
      <line x1={pad.l} y1={pad.t + h} x2={pad.l + dx} y2={pad.t + h + dy} stroke="#e2e8f0" strokeWidth="0.8" opacity="0">
        <animate attributeName="opacity" from="0" to="0.6" dur="0.4s" fill="freeze"/></line>
      <line x1={pad.l + w} y1={pad.t + h} x2={pad.l + w + dx} y2={pad.t + h + dy} stroke="#e2e8f0" strokeWidth="0.8" opacity="0">
        <animate attributeName="opacity" from="0" to="0.6" dur="0.4s" fill="freeze"/></line>
      {/* Grid */}
      {yTicks.map((t, i) => <g key={i} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${i * 0.06}s`} fill="freeze"/>
        <line x1={pad.l} y1={gy(t)} x2={pad.l + w} y2={gy(t)} stroke="#e2e8f0" strokeWidth={0.7} strokeDasharray="4 3"/>
        <line x1={pad.l + w} y1={gy(t)} x2={pad.l + w + dx} y2={gy(t) + dy} stroke="#e2e8f0" strokeWidth={0.4} strokeDasharray="3 3"/>
        <text x={pad.l - 6} y={gy(t) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{t}%</text></g>)}
      {data.map((d, i) => <text key={i} x={gx(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#94a3b8" fontWeight="500" opacity="0">
        <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${0.2 + i * 0.08}s`} fill="freeze"/>{d.name}</text>)}
      {/* Back shadows */}
      {series.map((k, ki) => <path key={`bs${ki}`} d={wavePath(k)} fill="none" stroke={fills[ki]} strokeWidth={3} opacity="0"
        transform={`translate(${dx},${dy})`}>
        <animate attributeName="opacity" from="0" to="0.08" dur="0.6s" begin={`${0.3 + ki * 0.15}s`} fill="freeze"/></path>)}
      {/* Depth connectors at data points */}
      {series.map((k, ki) => data.map((d, i) => <line key={`dc${ki}_${i}`} x1={gx(i)} y1={gy(d[k] || 0)} x2={gx(i) + dx} y2={gy(d[k] || 0) + dy}
        stroke={fills[ki]} strokeWidth={0.4} opacity="0">
        <animate attributeName="opacity" from="0" to="0.2" dur="0.3s" begin={`${0.5 + ki * 0.15}s`} fill="freeze"/></line>))}
      {/* Wave areas + lines */}
      {series.map((k, ki) => <g key={`wg${ki}`} filter="url(#wv3sh)">
        <path d={waveArea(k)} fill={`url(#wv3f${ki})`} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.7s" begin={`${0.4 + ki * 0.2}s`} fill="freeze"/></path>
        <path d={wavePath(k)} fill="none" stroke={fills[ki]} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={pathLen} strokeDashoffset={pathLen}>
          <animate attributeName="stroke-dashoffset" from={pathLen} to="0" dur="1.2s" begin={`${0.3 + ki * 0.2}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
        </path>
        {data.map((d, i) => d[k] > 0 && <circle key={i} cx={gx(i)} cy={gy(d[k])} r={0} fill="#fff" stroke={fills[ki]} strokeWidth={2.5}
          style={{ cursor: 'pointer' }} onMouseMove={e => onHover(e, d, k, ki)} onMouseLeave={() => setTip(null)}>
          <animate attributeName="r" from="0" to="5" dur="0.35s" begin={`${0.7 + ki * 0.2 + i * 0.08}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
        </circle>)}
        {data.map((d, i) => d[k] > 0 && <text key={`v${i}`} x={gx(i)} y={gy(d[k]) - 10} textAnchor="middle" fontSize="9" fontWeight="700" fill={fills[ki]} opacity="0">
          <animate attributeName="opacity" from="0" to="0.8" dur="0.3s" begin={`${0.9 + ki * 0.2 + i * 0.08}s`} fill="freeze"/>{d[k]}%</text>)}
      </g>)}
      {/* Legend */}
      <g transform={`translate(${pad.l}, ${H - 2})`} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.5s" begin="1.2s" fill="freeze"/>
        {series.map((k, ki) => <g key={ki} transform={`translate(${ki * 85}, 0)`}><rect y={-8} width={10} height={10} rx={2} fill={fills[ki]}/><text x={14} fontSize="10" fill="#64748b" fontWeight="600">{k}</text></g>)}</g>
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

/* ── 3D Horizontal Bar Chart – Custom SVG bars with depth ── */
const Bar3DHorizontal = ({ data }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 380, barH = 32, gap = 22, padL = 90, padR = 40, padT = 10;
  const H = padT + data.length * (barH + gap) + 30;
  const maxV = Math.max(...data.map(d => d.value), 1);
  const barW = W - padL - padR;
  const dx = 10, dy = -7;
  const darken = (hex, amt) => '#' + hex.replace('#', '').match(/../g).map(c => Math.max(0, Math.min(255, parseInt(c, 16) + amt)).toString(16).padStart(2, '0')).join('');
  const onHover = (e, d) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: d.stage, items: [{ name: 'Value', value: d.value, color: d.fill }] });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <filter id="b3dsh"><feDropShadow dx="1" dy="3" stdDeviation="3" floodOpacity="0.12"/></filter>
        {data.map((d, i) => <linearGradient key={i} id={`b3dg${i}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={darken(d.fill, 20)}/><stop offset="50%" stopColor={d.fill}/><stop offset="100%" stopColor={darken(d.fill, -15)}/></linearGradient>)}
      </defs>
      {/* Background grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
        const x = padL + barW * t;
        return <g key={i} opacity="0"><animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${i * 0.06}s`} fill="freeze"/>
          <line x1={x} y1={padT} x2={x} y2={padT + data.length * (barH + gap) - gap} stroke="#f1f5f9" strokeWidth={1}/>
          <text x={x} y={padT + data.length * (barH + gap) + 12} textAnchor="middle" fontSize="10" fill="#94a3b8">{Math.round(maxV * t)}</text></g>;
      })}
      {data.map((d, i) => {
        const y = padT + i * (barH + gap);
        const bw = (d.value / maxV) * barW;
        const front = `M${padL},${y} L${padL},${y + barH} L${padL + bw},${y + barH} L${padL + bw},${y} Z`;
        const topFace = `M${padL},${y} L${padL + dx},${y + dy} L${padL + bw + dx},${y + dy} L${padL + bw},${y} Z`;
        const sideFace = `M${padL + bw},${y} L${padL + bw + dx},${y + dy} L${padL + bw + dx},${y + barH + dy} L${padL + bw},${y + barH} Z`;
        return <g key={i} filter="url(#b3dsh)" opacity="0" style={{ cursor: 'pointer' }}
          onMouseMove={e => onHover(e, d)} onMouseLeave={() => setTip(null)}>
          <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.15 + i * 0.12}s`} fill="freeze"/>
          {/* Label */}
          <text x={padL - 8} y={y + barH / 2 + 4} textAnchor="end" fontSize="12" fontWeight="700" fill="#334155" opacity="0">
            <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${0.1 + i * 0.12}s`} fill="freeze"/>{d.stage}</text>
          {/* Top face */}
          <path d={`M${padL},${y} L${padL + dx},${y + dy} L${padL + dx},${y + dy} L${padL},${y} Z`} fill={darken(d.fill, -30)} opacity={0.85}>
            <animate attributeName="d" from={`M${padL},${y} L${padL + dx},${y + dy} L${padL + dx},${y + dy} L${padL},${y} Z`}
              to={topFace} dur="0.8s" begin={`${0.2 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
          </path>
          {/* Side face */}
          <path d={`M${padL},${y} L${padL + dx},${y + dy} L${padL + dx},${y + barH + dy} L${padL},${y + barH} Z`}
            fill={darken(d.fill, 45)} opacity={0.6}>
            <animate attributeName="d" from={`M${padL},${y} L${padL + dx},${y + dy} L${padL + dx},${y + barH + dy} L${padL},${y + barH} Z`}
              to={sideFace} dur="0.8s" begin={`${0.2 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
          </path>
          {/* Front face */}
          <rect x={padL} y={y} width={0} height={barH} rx={2} fill={`url(#b3dg${i})`}>
            <animate attributeName="width" from="0" to={bw} dur="0.8s" begin={`${0.2 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
          </rect>
          {/* Highlight stripe */}
          <rect x={padL} y={y} width={0} height={4} rx={1} fill="#fff" opacity={0.25}>
            <animate attributeName="width" from="0" to={bw} dur="0.8s" begin={`${0.2 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
          </rect>
          {/* Value label */}
          <text x={padL + bw + dx + 8} y={y + barH / 2 + 4} fontSize="11" fontWeight="700" fill={d.fill} opacity="0">
            <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${0.8 + i * 0.12}s`} fill="freeze"/>{d.value.toLocaleString()}</text>
        </g>;
      })}
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

/* ── Flow Map – Custom SVG campaign flow visualization ── */
const FlowMap = ({ data }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No data</div>;
  const W = 480, H = 280;
  const keys = ['Sent', 'Failed', 'Pending'];
  const keyColors = { Sent: '#00B4D8', Failed: '#1a1a18', Pending: '#9ec25a' };
  const lx = 10, lw = 100, rx = W - 80, rw = 65;
  const gap = 8;
  /* Source nodes (campaigns) */
  const totalAll = data.reduce((s, d) => s + (d.Sent || 0) + (d.Failed || 0) + (d.Pending || 0), 0) || 1;
  const usableH = H - 40;
  let sy = 20;
  const srcNodes = data.map((d, i) => {
    const val = (d.Sent || 0) + (d.Failed || 0) + (d.Pending || 0);
    const nh = Math.max(18, (val / totalAll) * usableH);
    const n = { ...d, val, y: sy, h: nh, idx: i };
    sy += nh + gap;
    return n;
  });
  /* Destination nodes (Sent/Failed/Pending) */
  const destTotals = keys.map(k => ({ name: k, val: data.reduce((s, d) => s + (d[k] || 0), 0), color: keyColors[k] })).filter(d => d.val > 0);
  const destTotal = destTotals.reduce((s, d) => s + d.val, 0) || 1;
  let dy2 = 20;
  const dstNodes = destTotals.map((d, i) => {
    const nh = Math.max(18, (d.val / destTotal) * usableH);
    const n = { ...d, y: dy2, h: nh };
    dy2 += nh + gap;
    return n;
  });
  /* Flow paths */
  const flows = [];
  const srcOffsets = srcNodes.map(() => 0);
  const dstOffsets = dstNodes.map(() => 0);
  srcNodes.forEach((src, si) => {
    keys.forEach(k => {
      const val = src[k] || 0;
      if (val <= 0) return;
      const di = dstNodes.findIndex(d => d.name === k);
      if (di < 0) return;
      const srcH = (val / src.val) * src.h;
      const dstH = (val / dstNodes[di].val) * dstNodes[di].h;
      flows.push({ si, di, val, color: keyColors[k],
        sy: src.y + srcOffsets[si], sh: srcH,
        ey: dstNodes[di].y + dstOffsets[di], eh: dstH });
      srcOffsets[si] += srcH;
      dstOffsets[di] += dstH;
    });
  });
  const cpx = (rx - lx - lw) * 0.45;
  const onHoverSrc = (e, n) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: n.name, items: keys.map(k => ({ name: k, value: n[k] || 0, color: keyColors[k] })).concat([{ name: 'Total', value: n.val }]) });
  };
  const onHoverDst = (e, n) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: n.name, items: [{ name: 'Total', value: n.val, color: n.color }] });
  };
  const onHoverFlow = (e, f) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: `${srcNodes[f.si].name} → ${dstNodes[f.di].name}`, items: [{ name: 'Count', value: f.val, color: f.color }] });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <filter id="fmSh"><feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/></filter>
        {flows.map((f, i) => <linearGradient key={i} id={`fm${i}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={f.color} stopOpacity={0.4}/><stop offset="100%" stopColor={f.color} stopOpacity={0.7}/></linearGradient>)}
        {srcNodes.map((n, i) => <linearGradient key={`sg${i}`} id={`fms${i}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={['#00B4D8','#5c7a1f','#9ec25a','#48CAE4'][i % 4]} stopOpacity="1"/>
          <stop offset="100%" stopColor={['#00B4D8','#5c7a1f','#9ec25a','#48CAE4'][i % 4]} stopOpacity="0.7"/></linearGradient>)}
      </defs>
      {/* Flow bands */}
      {flows.map((f, i) => {
        const x1 = lx + lw, x2 = rx;
        const d = `M${x1},${f.sy} C${x1+cpx},${f.sy} ${x2-cpx},${f.ey} ${x2},${f.ey} L${x2},${f.ey+f.eh} C${x2-cpx},${f.ey+f.eh} ${x1+cpx},${f.sy+f.sh} ${x1},${f.sy+f.sh} Z`;
        return <path key={i} d={d} fill={`url(#fm${i})`} stroke={f.color} strokeWidth={0.3} strokeOpacity={0.2} opacity="0"
          style={{ cursor: 'pointer' }} onMouseMove={e => onHoverFlow(e, f)} onMouseLeave={() => setTip(null)}>
          <animate attributeName="opacity" from="0" to="1" dur="0.7s" begin={`${0.3 + i * 0.08}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
          </path>;
      })}
      {/* Animated flow particles */}
      {flows.filter((_, i) => i < 6).map((f, i) => {
        const x1 = lx + lw, x2 = rx;
        const my = f.sy + f.sh / 2, mey = f.ey + f.eh / 2;
        const motionPath = `M${x1},${my} C${x1+cpx},${my} ${x2-cpx},${mey} ${x2},${mey}`;
        return <circle key={`p${i}`} r={3} fill={f.color} opacity={0}>
          <animate attributeName="opacity" values="0;0.6;0.6;0" dur="2.5s" begin={`${0.8 + i * 0.4}s`} repeatCount="indefinite"/>
          <animateMotion dur="2.5s" begin={`${0.8 + i * 0.4}s`} repeatCount="indefinite" path={motionPath}/>
        </circle>;
      })}
      {/* Source nodes */}
      {srcNodes.map((n, i) => <g key={`s${i}`} filter="url(#fmSh)" opacity="0" style={{ cursor: 'pointer' }}
        onMouseMove={e => onHoverSrc(e, n)} onMouseLeave={() => setTip(null)}>
        <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${i * 0.1}s`} fill="freeze"/>
        <rect x={lx} y={n.y + n.h / 2} width={lw} height={0} rx={5} fill={`url(#fms${i})`}>
          <animate attributeName="y" from={n.y + n.h / 2} to={n.y} dur="0.5s" begin={`${i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
          <animate attributeName="height" from="0" to={n.h} dur="0.5s" begin={`${i * 0.1}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
        </rect>
        <text x={lx + lw / 2} y={n.y + n.h / 2 + 4} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{n.name}</text></g>)}
      {/* Destination nodes */}
      {dstNodes.map((n, i) => <g key={`d${i}`} filter="url(#fmSh)" opacity="0" style={{ cursor: 'pointer' }}
        onMouseMove={e => onHoverDst(e, n)} onMouseLeave={() => setTip(null)}>
        <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.15 + i * 0.12}s`} fill="freeze"/>
        <rect x={rx} y={n.y + n.h / 2} width={rw} height={0} rx={5} fill={n.color}>
          <animate attributeName="y" from={n.y + n.h / 2} to={n.y} dur="0.5s" begin={`${0.15 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
          <animate attributeName="height" from="0" to={n.h} dur="0.5s" begin={`${0.15 + i * 0.12}s`} fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
        </rect>
        <text x={rx + rw / 2} y={n.y + n.h / 2 + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">{n.name}</text>
        <text x={rx + rw / 2} y={n.y + n.h / 2 + 14} textAnchor="middle" fontSize="8" fill="rgba(255,255,255,0.7)">{n.val}</text></g>)}
    </svg>
    <ChartTooltip tip={tip} />
    </div>
  );
};

const TIME_RANGES = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT — Tabbed Campaign Detail
   ════════════════════════════════════════════════════════════════ */
export default function UserCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');

  /* ── Campaign Detail State ── */
  const [campaign, setCampaign] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const [batches, setBatches] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(null);
  const [sunburstTip, setSunburstTip] = useState(null);
  const sunburstRef = React.useRef(null);
  const [dataSearch, setDataSearch] = useState('');
  const [downloadingPart, setDownloadingPart] = useState(null);

  /* ── Analytics State ── */
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  /* ── Performance State ── */
  const [perfData, setPerfData] = useState(null);
  const [perfDays, setPerfDays] = useState(7);
  const [perfLoading, setPerfLoading] = useState(false);

  /* ── Fetch Campaign ── */
  useEffect(() => {
    api.get(`/user/campaigns/${id}?page=${page}`)
      .then(({ data }) => {
        setCampaign(data.campaign);
        setStats({
          total: data.campaign?.myAssigned || 0,
          sent: data.campaign?.mySent || 0,
          failed: data.campaign?.myFailed || 0,
          pending: data.campaign?.myPending || 0
        });
        setData(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setSchedules(data.schedules || []);
        setBatches((data.schedules || []).flatMap(s => s.batches || []));
        setLoading(false);
      })
      .catch(() => { toast.error('Failed to load campaign'); setLoading(false); });
  }, [id, page]);

  /* ── Fetch Analytics on tab switch ── */
  useEffect(() => {
    if (activeTab !== 'analytics' || analyticsData) return;
    setAnalyticsLoading(true);
    api.get('/user/dashboard').then(({ data }) => { setAnalyticsData(data); setAnalyticsLoading(false); })
      .catch(() => { toast.error('Failed to load analytics'); setAnalyticsLoading(false); });
  }, [activeTab, analyticsData]);

  /* ── Fetch Performance ── */
  const fetchPerf = useCallback(async () => {
    setPerfLoading(true);
    try { const res = await api.get(`/user/performance?days=${perfDays}`); setPerfData(res.data); }
    catch { setPerfData(null); }
    finally { setPerfLoading(false); }
  }, [perfDays]);

  useEffect(() => {
    if (activeTab === 'performance') fetchPerf();
  }, [activeTab, fetchPerf]);

  /* ── Loading & Not Found ── */
  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading campaign...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );
  if (!campaign) return <div className="card" style={{ textAlign: 'center', padding: 40 }}>Campaign not found</div>;

  const totalMessages = (stats.sent || 0) + (stats.failed || 0) + (stats.pending || 0);
  const progress = totalMessages > 0 ? ((stats.sent / totalMessages) * 100).toFixed(1) : 0;
  const progressVal = parseFloat(progress);
  const progressColor = progressVal >= 80 ? COLORS.green : progressVal >= 50 ? COLORS.orange : COLORS.sky;

  const tabs = [
    { key: 'details', label: 'Details', icon: <FiFileText size={14} /> },
    { key: 'analytics', label: 'Analytics', icon: <FiBarChart2 size={14} /> },
    { key: 'performance', label: 'Performance', icon: <FiTrendingUp size={14} /> },
  ];

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <button className="btn btn-secondary" onClick={() => navigate('/user/campaigns')} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiArrowLeft size={14} /> Back
          </button>
          <h1 className="page-title">{campaign.name}</h1>
          <p className="page-subtitle">{campaign.type?.replace('_', ' ')} &bull; {campaign.status}</p>
        </div>
        <div style={{ padding: '8px 18px', borderRadius: 12, background: `linear-gradient(135deg, ${progressColor}20, ${progressColor}08)`, border: `1px solid ${progressColor}30`, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiArrowUpRight size={14} color={progressColor} />
          <span style={{ fontSize: 13, fontWeight: 700, color: progressColor }}>{progress}% Delivered</span>
        </div>
      </div>

      {/* Stat Cards — Always visible */}
      <div className="stats-grid">
        {[
          { icon: <FiDatabase />, value: stats.total || 0, label: 'Assigned', cls: 'blue', accent: '#00B4D8' },
          { icon: <FiClock />, value: stats.pending || 0, label: 'Pending', cls: 'yellow', accent: '#9ec25a' },
          { icon: <FiCheckCircle />, value: stats.sent || 0, label: 'Sent', cls: 'green', accent: '#5c7a1f' },
          { icon: <FiXCircle />, value: stats.failed || 0, label: 'Failed', cls: 'red', accent: '#1a1a18' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderBottom: `3px solid ${s.accent}`, animationDelay: `${0.05 + i * 0.08}s` }}
            onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--mouse-x', `${((e.clientX - r.left) / r.width) * 100}%`); e.currentTarget.style.setProperty('--mouse-y', `${((e.clientY - r.top) / r.height) * 100}%`); }}>
            <div className={`stat-card-icon ${s.cls}`}>{s.icon}</div>
            <div className="stat-card-value"><AnimatedNumber value={s.value} duration={1000 + i * 200} /></div>
            <div className="stat-card-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="tabs" style={{ marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.key}
            className={`tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════
         TAB: DETAILS
         ═══════════════════════════════════════ */}
      {activeTab === 'details' && (
        <div style={{ animation: 'fadeInUp 0.4s ease' }}>
          {/* Progress Bar */}
          <div className="card" style={{ marginBottom: 20, padding: '16px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Delivery Progress</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: progressColor }}>{progress}%</span>
            </div>
            <div style={{ height: 10, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 5, width: `${Math.min(progressVal, 100)}%`, background: `linear-gradient(90deg, ${progressColor}, ${progressColor}cc)`, transition: 'width 0.8s ease' }} />
            </div>
          </div>

          {/* Charts Row: Delivery Donut + Batch Bar */}
          {(() => {
            const pieData = [
              { name: 'Sent', value: stats.sent || 0, color: COLORS.green },
              { name: 'Failed', value: stats.failed || 0, color: COLORS.red },
              { name: 'Pending', value: stats.pending || 0, color: COLORS.orange },
            ].filter(d => d.value > 0);

            /* Sunburst data */
            const sunburstTotal = pieData.reduce((s, d) => s + d.value, 0) || 1;

            const batchBarData = batches.map(b => {
              const total = b.batchSize || 1;
              const isSent = b.status === 'sent';
              const isFailed = b.status === 'failed';
              return {
                name: `Batch ${b.batchNumber}`,
                Sent: isSent ? 100 : 0,
                Failed: isFailed ? 100 : 0,
                Pending: (!isSent && !isFailed) ? 100 : 0,
                records: total,
              };
            });

            return (
              <div style={{ display: 'grid', gridTemplateColumns: pieData.length > 0 ? '1fr 1.5fr' : '1fr', gap: 20, marginBottom: 20 }}>
                {pieData.length > 0 && (
                  <div className="card">
                    <h3 className="card-title" style={{ marginBottom: 8 }}>Delivery Breakdown</h3>
                    {/* Sunburst Diagram */}
                    <div ref={sunburstRef} style={{ position: 'relative' }} onMouseLeave={() => setSunburstTip(null)}>
                    <svg width="100%" height={240} viewBox="0 0 300 240">
                      <defs>
                        <filter id="sbSh"><feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12"/></filter>
                        {pieData.map((d, i) => (
                          <React.Fragment key={i}>
                            <linearGradient id={`sbG${i}`} x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor={d.color} stopOpacity={0.9}/>
                              <stop offset="100%" stopColor={d.color} stopOpacity={0.65}/>
                            </linearGradient>
                            <linearGradient id={`sbO${i}`} x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor={d.color} stopOpacity={0.5}/>
                              <stop offset="100%" stopColor={d.color} stopOpacity={0.25}/>
                            </linearGradient>
                          </React.Fragment>
                        ))}
                      </defs>
                      {(() => {
                        const cx = 150, cy = 115;
                        const innerR = 32, midR = 62, outerR = 90;
                        const gap = 0.02;
                        let angle = -Math.PI / 2;
                        const arcs = pieData.map((d, i) => {
                          const sweep = (d.value / sunburstTotal) * Math.PI * 2 - gap;
                          const a1 = angle + gap / 2;
                          const a2 = a1 + sweep;
                          angle += (d.value / sunburstTotal) * Math.PI * 2;
                          const large = sweep > Math.PI ? 1 : 0;
                          const arcPath = (r1, r2) => {
                            const x1i = cx + Math.cos(a1) * r1, y1i = cy + Math.sin(a1) * r1;
                            const x2i = cx + Math.cos(a2) * r1, y2i = cy + Math.sin(a2) * r1;
                            const x1o = cx + Math.cos(a1) * r2, y1o = cy + Math.sin(a1) * r2;
                            const x2o = cx + Math.cos(a2) * r2, y2o = cy + Math.sin(a2) * r2;
                            return `M${x1i},${y1i} A${r1},${r1} 0 ${large},1 ${x2i},${y2i} L${x2o},${y2o} A${r2},${r2} 0 ${large},0 ${x1o},${y1o} Z`;
                          };
                          const midAngle = (a1 + a2) / 2;
                          const labelR = outerR + 14;
                          const lx = cx + Math.cos(midAngle) * labelR;
                          const ly = cy + Math.sin(midAngle) * labelR;
                          return (
                            <g key={i} filter="url(#sbSh)" opacity="0" style={{ cursor: 'pointer' }}
                              onMouseMove={e => {
                                const rect = sunburstRef.current?.getBoundingClientRect();
                                if (!rect) return;
                                setSunburstTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: d.name, items: [{ name: 'Count', value: d.value, color: d.color }, { name: 'Share', value: Math.round((d.value / sunburstTotal) * 100) + '%' }] });
                              }} onMouseLeave={() => setSunburstTip(null)}>
                              <animate attributeName="opacity" from="0" to="1" dur="0.6s" begin={`${0.2 + i * 0.15}s`} fill="freeze"/>
                              <g transform={`rotate(0, ${cx}, ${cy})`}>
                                <animateTransform attributeName="transform" type="rotate" from={`-90 ${cx} ${cy}`} to={`0 ${cx} ${cy}`} dur="0.9s" begin={`${0.15 + i * 0.15}s`} fill="freeze" calcMode="spline" keySplines="0.4 0 0.2 1"/>
                                <path d={arcPath(innerR, midR)} fill={`url(#sbG${i})`} stroke="#fff" strokeWidth={1.5}/>
                                <path d={arcPath(midR + 2, outerR)} fill={`url(#sbO${i})`} stroke="#fff" strokeWidth={1}/>
                              </g>
                              <text x={lx} y={ly + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill={d.color} opacity="0">
                                <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.7 + i * 0.15}s`} fill="freeze"/>{d.name}</text>
                              <text x={lx} y={ly + 14} textAnchor="middle" fontSize="8" fill="#94a3b8" opacity="0">
                                <animate attributeName="opacity" from="0" to="1" dur="0.4s" begin={`${0.8 + i * 0.15}s`} fill="freeze"/>{d.value}</text>
                            </g>
                          );
                        });
                        return (
                          <>
                            {arcs}
                            <circle cx={cx} cy={cy} r={0} fill="#fff">
                              <animate attributeName="r" from="0" to={innerR - 2} dur="0.6s" begin="0.1s" fill="freeze" calcMode="spline" keySplines="0.34 1.56 0.64 1"/>
                            </circle>
                            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="16" fontWeight="800" fill="#0f172a" fontFamily="Poppins" opacity="0">
                              <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin="0.5s" fill="freeze"/>{sunburstTotal}</text>
                            <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fontWeight="600" fill="#94a3b8" letterSpacing="1" opacity="0">
                              <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin="0.6s" fill="freeze"/>TOTAL</text>
                          </>
                        );
                      })()}
                    </svg>
                    <ChartTooltip tip={sunburstTip} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 4 }}>
                      {pieData.map((d, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 12, height: 12, borderRadius: '50%', background: d.color, boxShadow: `0 2px 6px ${d.color}44` }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{d.name}: {d.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {batchBarData.length > 0 && (
                  <div className="card">
                    <h3 className="card-title" style={{ marginBottom: 8 }}>Batch Overview</h3>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '-4px 0 8px', fontWeight: 500 }}>3D wave surface</p>
                    <Wave3D data={batchBarData} />
                  </div>
                )}
              </div>
            );
          })()}

          {/* Message Previews */}
          {schedules.length > 0 && (() => {
            const cType = campaign.type || 'sms';
            const meta = TYPE_META[cType] || TYPE_META.sms;
            return (
              <>
                <div className="card" style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ color: meta.color, fontSize: 22 }}>{meta.icon}</span>
                    <h3 className="card-title" style={{ margin: 0 }}>Messages</h3>
                    <span className="badge" style={{ background: meta.color, color: '#fff' }}>{meta.label}</span>
                  </div>
                  {schedules.map((sched, si) => (
                    <div key={sched._id || si} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: 10, marginBottom: si < schedules.length - 1 ? 10 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: meta.color, fontSize: 16 }}>{meta.icon}</span>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>From: {sched.senderId}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                            {sched.messageContent?.substring(0, 80)}{sched.messageContent?.length > 80 ? '...' : ''}
                          </div>
                        </div>
                      </div>
                      <button className="btn btn-primary" style={{ background: meta.gradient || meta.color, border: 'none', fontSize: 13, padding: '8px 16px', color: '#fff', fontWeight: 600 }}
                        onClick={() => setShowPreview(sched)}>View Preview</button>
                    </div>
                  ))}
                </div>
                {showPreview && ReactDOM.createPortal(
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
                    onClick={() => setShowPreview(null)}>
                    <button onClick={() => setShowPreview(null)}
                      style={{ position: 'fixed', top: 18, right: 18, zIndex: 10001, width: 40, height: 40, borderRadius: '50%', background: '#fff', border: '2px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 20, color: '#64748b', fontWeight: 700 }}>×</button>
                    <div onClick={e => e.stopPropagation()} style={{ transform: 'scale(0.82)', transformOrigin: 'center center' }}>
                      <MessageTemplatePreview type={cType} sched={showPreview} campaignName={campaign?.name} />
                    </div>
                  </div>,
                  document.body
                )}
              </>
            );
          })()}

          {/* Batches Table */}
          {batches.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 className="card-title" style={{ marginBottom: 16 }}>Scheduled Batches</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>Batch #</th><th>Size</th><th>Status</th><th>Scheduled</th><th>Sent At</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {batches.map(b => (
                      <tr key={b._id}>
                        <td style={{ fontWeight: 600 }}>Batch {b.batchNumber}</td>
                        <td>{b.batchSize?.toLocaleString()}</td>
                        <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                        <td>{b.scheduledFor ? new Date(b.scheduledFor).toLocaleString() : '-'}</td>
                        <td>{b.sentAt ? new Date(b.scheduledFor || b.sentAt).toLocaleString() : <span style={{ color: '#999' }}>&mdash;</span>}</td>
                        <td>
                          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }}
                            onClick={() => navigate(`/user/campaigns/${id}/batch/${b._id}`)}>
                            View Data
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: 'var(--secondary-bg)', fontWeight: 600 }}>
                      <td>Total</td>
                      <td>{batches.reduce((sum, b) => sum + (b.batchSize || 0), 0).toLocaleString()}</td>
                      <td colSpan="4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <h3 className="card-title" style={{ margin: 0 }}>My Data (Masked)</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="badge badge-info" style={{ background: 'rgba(0,180,216,0.08)', color: '#00B4D8' }}>Data is masked for security</span>
                <button
                  className="btn btn-secondary"
                  disabled={downloadingPart === 1}
                  onClick={() => {
                    setDownloadingPart(1);
                    const link = document.createElement('a');
                    link.href = `/api/user/campaigns/${id}/export-csv?part=1`;
                    link.click();
                    setTimeout(() => setDownloadingPart(null), 3000);
                  }}
                  style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {downloadingPart === 1 ? '⏳ Preparing...' : '⬇ Download Part 1 (1–10L)'}
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={downloadingPart === 2}
                  onClick={() => {
                    setDownloadingPart(2);
                    const link = document.createElement('a');
                    link.href = `/api/user/campaigns/${id}/export-csv?part=2`;
                    link.click();
                    setTimeout(() => setDownloadingPart(null), 3000);
                  }}
                  style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {downloadingPart === 2 ? '⏳ Preparing...' : '⬇ Download Part 2 (10L+)'}
                </button>
                <div style={{ position: 'relative' }}>
                  <FiSearch size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="text" className="form-input" placeholder="Search..." value={dataSearch}
                    onChange={e => setDataSearch(e.target.value)} style={{ paddingLeft: 32, width: 180 }} />
                </div>
              </div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>#</th><th>Mobile</th><th>Name</th><th>Email</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {(() => {
                    const filteredData = dataSearch
                      ? data.filter(d => (d.mobile || '').includes(dataSearch) || (d.name || '').toLowerCase().includes(dataSearch.toLowerCase()))
                      : data;
                    return filteredData.length > 0 ? filteredData.map((d, i) => (
                      <tr key={d._id}>
                        <td>{d.serial || (page - 1) * 50 + i + 1}</td>
                        <td style={{ fontFamily: 'monospace' }}>{d.mobile || '-'}</td>
                        <td>{d.name || '-'}</td>
                        <td>{d.email || '-'}</td>
                        <td><span className={`badge badge-${d.status}`}>{d.status}</span></td>
                      </tr>
                    )) : (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No data found</td></tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Page {page} of {totalPages}</span>
                <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, marginTop: 10 }}>
              Showing 50 randomly sampled records · Use Download buttons above for full export
            </p>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════
         TAB: ANALYTICS
         ═══════════════════════════════════════ */}
      {activeTab === 'analytics' && (
        <div style={{ animation: 'fadeInUp 0.4s ease' }}>
          {analyticsLoading ? (
            <div className="loading-spinner">
              <div className="spinner" />
              <div className="loading-text">Loading analytics...</div>
              <div className="loading-dots"><span /><span /><span /></div>
            </div>
          ) : (() => {
            const aStats = analyticsData?.stats || {};
            const recentCampaigns = analyticsData?.recentCampaigns || [];
            const channelDataRaw = analyticsData?.channelData || [];

            const aTotalMsg = (aStats.totalSent || 0) + (aStats.totalFailed || 0) + (aStats.totalPending || 0);
            const deliveryRate = aTotalMsg > 0 ? ((aStats.totalSent / aTotalMsg) * 100).toFixed(1) : 0;
            const failRate = aTotalMsg > 0 ? ((aStats.totalFailed / aTotalMsg) * 100).toFixed(1) : 0;
            const pendingRate = aTotalMsg > 0 ? ((aStats.totalPending / aTotalMsg) * 100).toFixed(1) : 0;

            let trendData = recentCampaigns.slice(0, 8).map((c, i) => ({
              name: c.name?.substring(0, 10) || `C${i + 1}`,
              Delivered: c.mySent || 0, Failed: c.myFailed || 0,
            }));
            if (trendData.length === 1) {
              trendData = [{ name: '', Delivered: 0, Failed: 0 }, trendData[0], { name: ' ', Delivered: 0, Failed: 0 }];
            } else if (trendData.length === 2) {
              trendData = [{ name: '', Delivered: 0, Failed: 0 }, ...trendData];
            }

            const channelPie = channelDataRaw.map(ch => ({
              name: TYPE_LABELS[ch.type] || ch.type,
              value: (ch.sent || 0) + (ch.failed || 0),
              color: TYPE_COLORS[ch.type] || '#c9c9c3'
            })).filter(c => c.value > 0);
            if (channelPie.length === 0) channelPie.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
            const channelTotal = channelPie.reduce((a, c) => a + c.value, 0);

            const funnelData = [
              { stage: 'Assigned', value: aStats.totalAssigned || 0, fill: COLORS.sky },
              { stage: 'Processed', value: (aStats.totalSent || 0) + (aStats.totalFailed || 0), fill: '#9ec25a' },
              { stage: 'Delivered', value: aStats.totalSent || 0, fill: COLORS.green },
              { stage: 'Failed', value: aStats.totalFailed || 0, fill: COLORS.red },
              { stage: 'Pending', value: aStats.totalPending || 0, fill: COLORS.orange },
            ].filter(d => d.value > 0);

            const comparisonData = recentCampaigns.map(c => ({
              name: (c.name || '').substring(0, 12),
              Sent: c.mySent || 0, Failed: c.myFailed || 0, Pending: c.myPending || 0,
            }));

            const gaugeVal = parseFloat(deliveryRate);
            const gaugeData = [{ name: 'Rate', value: gaugeVal, fill: gaugeVal >= 90 ? COLORS.green : gaugeVal >= 70 ? COLORS.orange : COLORS.red }];

            /* Stream graph data — organic wave flow from analytics stats */
            const sgTotal = (aStats.totalSent || 0) + (aStats.totalFailed || 0) + (aStats.totalPending || 0);
            const streamData = sgTotal > 0 ? Array.from({ length: 8 }, (_, i) => ({
              t: i, Delivered: Math.round((aStats.totalSent || 0) * (1 + Math.sin(i * 0.7) * 0.3) / 8),
              Failed: Math.round((aStats.totalFailed || 0) * (1 + Math.cos(i * 0.9) * 0.25) / 8),
              Pending: Math.round((aStats.totalPending || 0) * (1 + Math.sin(i * 1.1 + 1) * 0.2) / 8),
            })) : [];

            const kpis = [
              { icon: <FiThumbsUp />, value: `${deliveryRate}%`, label: 'Delivery Rate', color: '#00B4D8', bg: '#eef3e6' },
              { icon: <FiTrendingUp />, value: `${failRate}%`, label: 'Failure Rate', color: '#1a1a18', bg: '#f0f0ed' },
              { icon: <FiTarget />, value: `${pendingRate}%`, label: 'Pending Rate', color: '#5c7a1f', bg: '#e5edda' },
              { icon: <FiPercent />, value: aTotalMsg.toLocaleString(), label: 'Total Messages', color: '#1a1a18', bg: '#f4f4f3' },
            ];

            return (
              <>
                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                  {kpis.map((k, i) => (
                    <div key={i} className="stat-card" style={{ borderBottom: `3px solid ${k.color}`, position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: -10, right: -10, width: 60, height: 60, borderRadius: '50%', background: k.bg, opacity: 0.5 }} />
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: k.bg, color: k.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, marginBottom: 10 }}>
                          {k.icon}
                        </div>
                        <div className="stat-card-value">{k.value}</div>
                        <div className="stat-card-label">{k.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Row 1: Trend + Hive Plot + Stream Graph */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr', gap: 20, marginBottom: 20 }}>
                  <div className="chart-container">
                    <SectionHeader title="Performance Trend" subtitle="Delivered vs Failed across campaigns" />
                    {trendData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 8" stroke="#e9ecef" vertical={false} />
                          <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip content={<GlassTooltip />} />
                          <Legend content={<CustomLegend />} />
                          <Line type="natural" dataKey="Delivered" stroke="#00B4D8" strokeWidth={3.5}
                            dot={{ r: 6, fill: '#fff', stroke: '#00B4D8', strokeWidth: 3 }}
                            activeDot={{ r: 9, fill: '#00B4D8', stroke: '#fff', strokeWidth: 3 }}
                            animationDuration={1200} animationEasing="ease-in-out" />
                          <Line type="natural" dataKey="Failed" stroke="#1a1a18" strokeWidth={2.5} strokeDasharray="8 4"
                            dot={{ r: 5, fill: '#fff', stroke: '#1a1a18', strokeWidth: 2.5 }}
                            activeDot={{ r: 8, fill: '#1a1a18', stroke: '#fff', strokeWidth: 3 }}
                            animationDuration={1500} animationEasing="ease-in-out" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="empty-state"><div className="empty-state-icon">📈</div><h3>No data yet</h3></div>
                    )}
                  </div>

                  {/* Hive Plot — Channel network */}
                  <div className="chart-container">
                    <SectionHeader title="Channel Split" subtitle="Hive network" />
                    <HivePlot data={channelPie} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0', justifyContent: 'center' }}>
                      {channelPie.map((d, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 8, background: 'var(--bg-primary)' }}>
                          <span style={{ width: 8, height: 8, borderRadius: 3, background: d.color }} />
                          <span style={{ fontSize: 10, fontWeight: 600, color: '#334155' }}>{d.name}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>{d.value?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sorted Stream Graph — Delivery flow */}
                  <div className="chart-container">
                    <SectionHeader title="Rate" subtitle="Sorted stream" />
                    {streamData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={streamData} stackOffset="wiggle" margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                          <defs>
                            <linearGradient id="sgDel" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#00B4D8" stopOpacity={0.7}/><stop offset="100%" stopColor="#00B4D8" stopOpacity={0.3}/>
                            </linearGradient>
                            <linearGradient id="sgFail" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.6}/><stop offset="100%" stopColor="#1a1a18" stopOpacity={0.2}/>
                            </linearGradient>
                            <linearGradient id="sgPend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#9ec25a" stopOpacity={0.6}/><stop offset="100%" stopColor="#9ec25a" stopOpacity={0.2}/>
                            </linearGradient>
                          </defs>
                          <Tooltip content={<GlassTooltip />} />
                          <Area type="natural" dataKey="Delivered" stroke="#00B4D8" fill="url(#sgDel)" strokeWidth={2} />
                          <Area type="natural" dataKey="Failed" stroke="#1a1a18" fill="url(#sgFail)" strokeWidth={1.5} />
                          <Area type="natural" dataKey="Pending" stroke="#9ec25a" fill="url(#sgPend)" strokeWidth={1.5} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No data</h3></div>
                    )}
                    <div style={{ textAlign: 'center', marginTop: 4 }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: gaugeData[0].fill, fontFamily: 'Poppins' }}>{deliveryRate}%</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>DELIVERY</div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Funnel + Comparison */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20 }}>
                  <div className="chart-container">
                    <SectionHeader title="Message Funnel" subtitle="3D processing pipeline" />
                    {funnelData.length > 0 ? (
                      <Bar3DHorizontal data={funnelData} />
                    ) : (
                      <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No funnel data</h3></div>
                    )}
                  </div>

                  {/* Flow Map — Campaign Comparison */}
                  <div className="chart-container">
                    <SectionHeader title="Campaign Comparison" subtitle="Flow map" />
                    {comparisonData.length > 0 ? (
                      <FlowMap data={comparisonData} />
                    ) : (
                      <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No comparison data</h3></div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ═══════════════════════════════════════
         TAB: PERFORMANCE
         ═══════════════════════════════════════ */}
      {activeTab === 'performance' && (
        <div style={{ animation: 'fadeInUp 0.4s ease' }}>
          {perfLoading ? (
            <div className="loading-spinner">
              <div className="spinner" />
              <div className="loading-text">Loading performance...</div>
              <div className="loading-dots"><span /><span /><span /></div>
            </div>
          ) : !perfData ? (
            <div className="card" style={{ textAlign: 'center', padding: 40 }}>No performance data available</div>
          ) : (() => {
            const { stats: pStats, trend, channelBreakdown, campaignLeaderboard } = perfData;
            const successRate = pStats.totalSent + pStats.totalFailed > 0
              ? ((pStats.totalSent / (pStats.totalSent + pStats.totalFailed)) * 100).toFixed(1) : 0;

            const statCards = [
              { label: 'Assigned', value: pStats.totalAssigned?.toLocaleString(), icon: <FiSend />, color: COLORS.sky },
              { label: 'Sent', value: pStats.totalSent?.toLocaleString(), icon: <FiCheckCircle />, color: COLORS.green },
              { label: 'Failed', value: pStats.totalFailed?.toLocaleString(), icon: <FiXCircle />, color: COLORS.red },
              { label: 'Success Rate', value: `${successRate}%`, icon: <FiTrendingUp />, color: COLORS.ink },
            ];

            const pieData = (channelBreakdown || []).map(c => ({ name: c.type, value: c.sent + c.failed }));

            return (
              <>
                {/* Stat Cards */}
                <div className="stats-grid" style={{ marginBottom: 20 }}>
                  {statCards.map((s, i) => (
                    <div key={i} className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 12, background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, fontSize: 18 }}>{s.icon}</div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{s.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Arc Diagram + 3D Funnel */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
                  <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.sky, fontSize: 16 }}><FiTrendingUp /></div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>Delivery Trend</h3>
                      </div>
                      <select value={perfDays} onChange={e => setPerfDays(+e.target.value)} className="form-input" style={{ width: 'auto', fontSize: 13, padding: '6px 12px' }}>
                        {TIME_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <ArcDiagram sent={pStats.totalSent || 0} failed={pStats.totalFailed || 0} assigned={pStats.totalAssigned || 0} />
                    <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginTop: 8 }}>
                      {[{ label: 'Assigned', color: '#00B4D8' }, { label: 'Sent', color: '#9ec25a' }, { label: 'Failed', color: '#1a1a18' }].map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />{item.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.sky, fontSize: 16 }}><FiSend /></div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>Channels</h3>
                    </div>
                    <Funnel3D data={pieData.map((d, i) => ({ ...d, color: PIE_COLORS[i % PIE_COLORS.length] }))} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 4 }}>
                      {(pieData || []).slice(0, 6).map((d, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 8, background: 'var(--bg-primary)', fontSize: 11 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 3, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span style={{ fontWeight: 600, color: '#334155' }}>{d.name || d.type}</span>
                          <strong style={{ color: '#0f172a' }}>{d.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Alluvial Diagram + Leaderboard */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.sky, fontSize: 16 }}><FiTrendingUp /></div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>Campaign Comparison</h3>
                    </div>
                    <AlluvialDiagram data={(campaignLeaderboard || []).slice(0, 6)} />
                  </div>

                  <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.sky, fontSize: 16 }}><FiCheckCircle /></div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>Campaign Leaderboard</h3>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: 'auto', fontSize: 13 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Campaign</th>
                            <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>Sent</th>
                            <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(campaignLeaderboard || []).map((c, i) => {
                            const total = c.sent + c.failed;
                            const rate = total > 0 ? ((c.sent / total) * 100).toFixed(1) : 0;
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '10px 6px', fontWeight: 600, color: '#0f172a', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</td>
                                <td style={{ textAlign: 'right', padding: '10px 6px', color: '#64748b' }}>{c.sent}</td>
                                <td style={{ textAlign: 'right', padding: '10px 6px' }}>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                    background: rate >= 80 ? '#eef3e6' : rate >= 50 ? '#e5edda' : '#f0f0ed',
                                    color: rate >= 80 ? '#5c7a1f' : rate >= 50 ? '#6b8f27' : '#1a1a18',
                                  }}>{rate}%</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
